import {
  PricingModelFragment,
  PurchasedEnergyType,
} from '../../generated/graphql'
import {
  purchasedEnergySubCategories,
  soldElectricitySubCategory,
} from '../../provider/result-categories'
import { isBuilding } from '../BuildingCalculator'
import { getFilteredResultsFromContainers } from '../calculator-result-utility'
import { SystemContainerCalculator } from '../SystemContainerCalculator'
import { subtractProfitsFromCosts } from './calculated-cost-utility'
import {
  CalculatedEnergyCost,
  createMapEnergyResultToCost,
  EnergyCostType,
} from './cost-calculator'

export class ContainerCostCalculator {
  private energyCosts: CalculatedEnergyCost[] = []

  private energyProfit = {} as CalculatedEnergyCost

  public constructor(
    private readonly pricingModel: PricingModelFragment,
    private readonly energyCalculator: SystemContainerCalculator,
  ) {}

  public calculateCosts(): void {
    const purchasedEnergyResults = purchasedEnergySubCategories.map(
      (filter) => ({
        energyType: filter.purchasedEnergyType,
        hourlyValues: getFilteredResultsFromContainers(filter, [
          this.energyCalculator,
        ]).values,
      }),
    )

    const mapEnergyResultToCost = createMapEnergyResultToCost(
      this.pricingModel,
      false,
    )
    const energyCosts = purchasedEnergyResults.map(mapEnergyResultToCost)
    this.energyCosts = energyCosts

    const filter = soldElectricitySubCategory
    const soldElectricityResult = getFilteredResultsFromContainers(filter, [
      this.energyCalculator,
    ])
    const mapSoldEnergyResultToCost = createMapEnergyResultToCost(
      this.pricingModel,
      true,
    )

    const profit = mapSoldEnergyResultToCost({
      energyType: PurchasedEnergyType.Electricity,
      hourlyValues: soldElectricityResult.values,
    })
    this.energyProfit = profit
  }

  public getEnergyCosts(): CalculatedEnergyCost[] {
    return this.energyCosts.map((energyCost) => {
      if (energyCost.energyType === PurchasedEnergyType.Electricity) {
        return subtractProfitsFromCosts(energyCost, this.energyProfit)
      }
      return energyCost
    })
  }

  public getEnergyCostsAndProfit(): CalculatedEnergyCost[] {
    const costs: CalculatedEnergyCost[] = this.energyCosts.map((energyCost) => {
      if (energyCost.energyType === PurchasedEnergyType.Electricity) {
        return { ...energyCost, energyType: EnergyCostType.ElectricityCost }
      }
      return energyCost
    })
    return [
      ...costs,
      { ...this.energyProfit, energyType: EnergyCostType.ElectricityProfit },
    ]
  }

  public getId(): string {
    return this.energyCalculator.id
  }

  public getMultiplier(): number | null {
    return isBuilding(this.energyCalculator)
      ? this.energyCalculator.multiplier
      : null
  }

  public getProviderIds(): string[] {
    return this.energyCalculator.getProviders().map((provider) => provider.id)
  }
}

export function createMapContainerToCostCalculator(
  pricingModel: PricingModelFragment,
) {
  return (container: SystemContainerCalculator): ContainerCostCalculator =>
    new ContainerCostCalculator(pricingModel, container)
}
